import { From, Join } from '@mode-switch/model';
import {
  ChartTypes,
  DataViewTypes,
  DatasetBrowserData,
  FlamingoTypes,
  PublicationState,
  SwitchAttrsDomain,
  SwitchViewDomain,
} from '@mode/shared/contract-common';
import { QueryDomain } from '@mode/shared/contract-common';
import { Observable } from 'rxjs';

export abstract class DataViewFacade {
  abstract model$: Observable<FlamingoTypes.ModeModel>;
  abstract modelForUnifiedPublish$: Observable<FlamingoTypes.ModeModel>;
  abstract datasetResults$: Observable<DataViewTypes.DatasetResult[]>;
  abstract successfulDatasets$: Observable<DataViewTypes.DatasetResult[]>;
  abstract successfulEnhancedDatasets$: Observable<DataViewTypes.EnhancedDatasetResult[]>;
  abstract importedDatasetCount$: Observable<number>;
  abstract importedDatasetResults$: Observable<DataViewTypes.DatasetResult[]>;
  abstract enhancedDatasetResults$: Observable<DataViewTypes.EnhancedDatasetResult[]>;
  abstract successfulEnhancedQueries$: Observable<DataViewTypes.EnhancedDatasetResult[]>;
  abstract localSwitchViewTokens$: Observable<string[]>;
  abstract localSwitchViewCount$: Observable<number>;
  abstract attrUpdated$: Observable<{ switchViewToken: string | undefined; attrToken: string }>;
  abstract initialEditorSwitchViewsLoaded$: Observable<boolean>;
  abstract isRemoveDatasetError$: Observable<DataViewTypes.DatasetResult>;
  abstract canDeleteSwitchView$: Observable<boolean>;
  abstract getDatasetsLimitExceeded$: Observable<boolean>;
  abstract switchViewTokens$: Observable<string[] | number[]>;
  abstract createDatasetBySwitchViewToken(switchViewToken: string): Observable<DataViewTypes.DatasetResult | undefined>;
  abstract createIsDraftBySwitchViewToken(switchViewToken: string): Observable<boolean>;
  abstract createEnhancedDatasetResultBySwitchViewToken(
    switchViewToken: string
  ): Observable<DataViewTypes.EnhancedDatasetResult | undefined>;
  abstract createSwitchViewFeatureBySwitchViewToken(
    switchViewToken: string
  ): Observable<SwitchViewDomain.SwitchView | undefined>;
  abstract createRunReadyByQueryToken(queryToken: string): Observable<boolean | undefined>;
  abstract createRunReadyOrRunningByQueryToken(queryToken: string): Observable<boolean | undefined>;
  abstract createColumnsSwitchModelByQueryToken(queryToken: string): Observable<FlamingoTypes.ModeModel | undefined>;
  abstract createColumnsAndAttrsSwitchModelByQueryToken(
    queryToken: string,
    switchViewToken: string
  ): Observable<FlamingoTypes.ModeModel | undefined>;
  abstract createSwitchViewBySwitchViewToken(switchViewToken: string): Observable<
    | {
        sourceType: DataViewTypes.SwitchViewSourceType;
        name: string;
        desc?: string | undefined;
        from: From;
        joins?: readonly Join[] | undefined;
        attrs: ReadonlyArray<FlamingoTypes.ModeAttr>;
      }
    | undefined
  >;
  abstract createQueryRunTokenByQueryToken(queryToken: string): Observable<string | undefined>;
  abstract createQueryTokenBySwitchViewToken(switchViewToken: string): Observable<string | undefined>;
  abstract createSwitchViewTokenByQueryToken(queryToken: string): Observable<string | undefined>;
  abstract createQueryRunTokenByQueryTokenNoEmpty(queryToken: string): Observable<string>;
  abstract createRootSourceModelNameBySwitchViewToken(switchViewToken: string): Observable<string>;
  abstract createRunReadyWithFailedByQueryToken(queryToken: string): Observable<boolean | undefined>;
  abstract createQueryRunStateByQueryTokenNoEmpty(queryToken: string): Observable<DataViewTypes.QueryRunStatus>;
  abstract getDatasetResultCompletedAtBySwitchViewToken(switchViewToken: string): Observable<string | undefined>;
  abstract getBytesSizeBySwitchViewToken(switchViewToken: string): Observable<number | null | undefined>;
  abstract getCsvUrlBySwitchViewToken(switchViewToken: string): Observable<string | undefined>;
  abstract getLegacyContentBySwitchViewToken(switchViewToken: string): Observable<any[] | undefined>;
  abstract getMetaColumnsBySwitchViewToken(
    switchViewToken: string
  ): Observable<DataViewTypes.DatasetResourceColumn[] | undefined>;
  abstract getRawQueryBySwitchViewToken(switchViewToken: string): Observable<string | undefined>;
  abstract getRawQueryByQueryToken(queryToken: string): Observable<string | undefined>;
  abstract getRenderedQueryByViewToken(switchViewToken: string): Observable<string | undefined>;
  abstract getRenderedQueryByQueryToken(queryToken: string): Observable<string | undefined>;
  abstract getErrorByQueryToken(queryToken: string): Observable<string | null | undefined>;
  abstract getRowCountBySwitchViewToken(switchViewToken: string): Observable<number | null | undefined>;
  abstract getQueryRunStateBySwitchViewToken(
    switchViewToken: string
  ): Observable<DataViewTypes.QueryRunStatus | undefined>;
  abstract getDatasetResultByQuery(token: string): Observable<DataViewTypes.DatasetResult | undefined>;
  abstract getPublishDatasetResultBySwitchView(token: string): Observable<DataViewTypes.PublishResult | undefined>;
  abstract getDatasetPublicationStateBySwitchView(token: string): Observable<PublicationState | undefined>;
  abstract isPublishedDatasetBySwitchView(token: string): Observable<boolean>;
  abstract getDatasetResultByQueryNoEmpty(token: string): Observable<DataViewTypes.DatasetResult>;
  abstract getDatasetResultByQueryRunToken(queryRunToken: string): Observable<DataViewTypes.DatasetResult | undefined>;
  abstract getLocalSwitchViewAttrFeaturesBySwitchViewToken(
    switchViewToken: string
  ): Observable<SwitchAttrsDomain.SwitchAttr[]>;
  abstract isCalcBySwitchViewTokenFromSwitchViewAttr(switchViewToken: string): Observable<boolean>;
  abstract getExecutionTimeBySwitchViewToken(switchViewToken: string): Observable<string>;
  abstract getSwitchViewNameBySwitchViewToken(switchViewToken: string): Observable<string | undefined>;
  abstract getQueryNameBySwitchViewToken(switchViewToken: string): Observable<string | undefined>;
  abstract getIsFallbackDataBySwitchViewToken(switchViewToken: string): Observable<boolean | undefined>;
  abstract getIsPurgedBySwitchViewToken(switchViewToken: string): Observable<boolean | undefined>;
  abstract getDatasourceIdBySwitchViewToken(switchViewToken: string): Observable<number | null | undefined>;
  abstract getJsonUrlBySwitchViewToken(switchViewToken: string): Observable<string | undefined>;
  abstract getModelFormatsByQueryToken(queryToken: string): Observable<{
    format: ChartTypes.ChartFormat;
    fieldsFormat: ChartTypes.FieldFormatMap;
  }>;
  abstract loadExplorationDataView(dataView: SwitchViewDomain.DataView): void;
  abstract loadVisualizationDataView(dataView: SwitchViewDomain.DataView): void;
  abstract loadGfDataViews(dataViews: ReadonlyArray<SwitchViewDomain.DataView>): void;
  abstract loadReportViewDataViews(dataViews: ReadonlyArray<SwitchViewDomain.DataView>): void;
  abstract startPolling(
    accountId: string,
    reportToken: string,
    reportRunToken: string,
    viewTokens: ReadonlyArray<string>
  ): void;
  abstract startEditorPolling(reportToken: string, reportRunToken: string, viewTokens: ReadonlyArray<string>): void;
  abstract setPendingDatasetResultByQueryToken(queryToken: string): void;
  abstract setPendingAllLocalDatasetResults(): void;
  abstract deleteSwitchViewResourcesFromCache(viewTokens: string[]): void;
  abstract queryViewDetailsResults(queryToken: string): void;
  abstract queryViewDetailsResultsAngularJs(queryToken: string): void;
  abstract queryDetailsResultsWithCalcs(queryToken: string): void;
  abstract openCalcEditor(
    switchViewToken: string,
    viewName: string,
    viewMode: 'static' | 'edit',
    calc: FlamingoTypes.ModeCalc | undefined,
    readOnly: boolean | undefined,
    duplicate: boolean | undefined,
    isImportedDataset: boolean | undefined,
    canShowFieldDescription: boolean | undefined
  ): void;
  abstract deleteCalcField(attr: FlamingoTypes.ModeCalc, token: string, queryToken: string): void;
  abstract getEditorSwitchViews(reportToken: string): void;
  abstract setSwitchViewRef(refId: string, switchViewToken: string): void;
  abstract setSwitchViewRefs(refs: ReadonlyArray<{ refId: string; switchViewToken: string }>): void;
  abstract deleteSwitchViewRef(refId: string): void;
  abstract deleteSwitchViewRefs(refIds: ReadonlyArray<string>): void;
  abstract deleteEditorSwitchView(token: string): void;
  abstract updateLocalQuerySwitchViewTitle(token: string, title: string): void;
  abstract addTemporaryLocalQuery(query: QueryDomain.Query, isMetric: boolean): void;
  abstract updateTemporaryLocalQueryToken(tempQueryToken: string, queryToken: string): void;
  abstract removeDatasetFromTab(accountId: string, reportId: string, switchViewToken: string): void;
  abstract removeDatasetFromSideNav(accountId: string, reportId: string, switchViewToken: string): void;
  abstract clearDatasetFailure(switchViewToken: string, error: null): void;
  abstract datasetRefreshButtonClicked(reportToken: string, switchViewToken: string): void;
  abstract datasetRefreshAllClicked(reportToken: string, switchViewTokens: string[]): void;
  abstract getDatasetWithSwitchViewToken(accountId: string, datasetToken: string): Observable<DatasetBrowserData>;
  abstract openDuplicateDialog(onDuplicateConfirm: () => void, isSecretExist: boolean, isPublished: boolean): void;
  abstract triggerPublishProgressToast(switchViewToken: string): void;
  abstract hidePublishToast(switchViewToken: string, progressToastId: number): void;
  abstract setActiveDataset(switchViewToken: string | undefined): void;
  abstract getActiveSwitchViewToken(): Observable<string | null>;
}
